import React, { useState } from 'react';
import { Pencil, Check, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useContent } from '../context/ContentContext';
import { useToast } from '../hooks/use-toast';

/**
 * One-line admin-editable text (phone number, address, email, hours, etc.).
 * Lightweight sibling of `EditableText` — no rich text, no dialog, just a
 * small inline input that appears when the admin clicks the pencil.
 *
 * Props:
 *   contentKey      Mongo key for the value (shared storage with EditableText)
 *   defaultValue    fallback when the admin hasn't customized it yet
 *   as              element to render — 'span' | 'a' (default 'span')
 *   href            if provided and `as='a'`, used as the link href. If the
 *                   href starts with `tel:` or `mailto:`, we auto-append the
 *                   current value so the link stays in sync when edited.
 *   className       class(es) on the rendered element
 *   label           friendly label in the toast
 *   placeholder     input placeholder while editing
 */
const EditableInline = ({
  contentKey,
  defaultValue = '',
  as = 'span',
  href,
  className = '',
  label,
  placeholder = '',
}) => {
  const { isAdmin } = useAuth();
  const { getContent, saveContent } = useContent();
  const { toast } = useToast();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);

  const value = getContent(contentKey, defaultValue);

  const start = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    setDraft(value || '');
    setEditing(true);
  };

  const cancel = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    setEditing(false);
  };

  const save = async (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    const trimmed = (draft || '').trim();
    if (trimmed === value) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await saveContent(contentKey, trimmed);
      toast({ title: 'Saved', description: label ? `${label} updated.` : 'Content updated.' });
      setEditing(false);
    } catch (err) {
      toast({
        title: 'Save failed',
        description: err?.response?.data?.detail || 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <span className="inline-flex items-center gap-1" data-testid={`editable-inline-${contentKey}-edit`}>
        <input
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') save(e);
            else if (e.key === 'Escape') cancel(e);
          }}
          placeholder={placeholder}
          className="px-2 py-0.5 border border-[#a68a5b] bg-white text-[#2a1a45] text-sm min-w-[12rem] focus:outline-none focus:ring-1 focus:ring-[#a68a5b]"
          data-testid={`editable-inline-${contentKey}-input`}
        />
        <button
          type="button"
          onClick={save}
          disabled={saving}
          aria-label="Save"
          className="p-1 bg-[#3a2359] text-white hover:bg-[#2a1a45] disabled:opacity-60"
          data-testid={`editable-inline-${contentKey}-save`}
        >
          <Check size={14} />
        </button>
        <button
          type="button"
          onClick={cancel}
          aria-label="Cancel"
          className="p-1 border border-[#a68a5b] text-[#2a1a45] bg-white hover:bg-[#f5efe0]"
          data-testid={`editable-inline-${contentKey}-cancel`}
        >
          <X size={14} />
        </button>
      </span>
    );
  }

  // Non-editing render. If rendered as a link, keep the tel:/mailto: href in
  // sync with the current value so edits flow through to clickable links.
  const linkHref = (() => {
    if (as !== 'a' || !href) return href;
    if (href === 'tel:' || href === 'tel:auto') return `tel:${(value || '').replace(/[^\d+]/g, '')}`;
    if (href === 'mailto:' || href === 'mailto:auto') return `mailto:${value || ''}`;
    return href;
  })();

  const inner = (
    <>
      {value}
      {isAdmin && (
        <button
          type="button"
          onClick={start}
          aria-label={`Edit ${label || contentKey}`}
          className="ml-1.5 inline-flex items-center justify-center opacity-70 hover:opacity-100 text-current"
          data-testid={`editable-inline-${contentKey}-pencil`}
        >
          <Pencil size={12} />
        </button>
      )}
    </>
  );

  if (as === 'a') {
    return (
      <a href={linkHref} className={className} data-testid={`editable-inline-${contentKey}`}>
        {inner}
      </a>
    );
  }
  return (
    <span className={className} data-testid={`editable-inline-${contentKey}`}>
      {inner}
    </span>
  );
};

export default EditableInline;
